import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import { GridColDef } from '@mui/x-data-grid';
import { IAnalyticsColumnMeta } from '../../types/api/response/analyticsResponse';

/**
 * Column-metadata-driven rendering: the analytics catalog declares each column's key,
 * label and type, and this maps them to DataGrid columns with consistent formatting.
 * Every current AND future report renders through this — one styling decision, applied
 * everywhere (Justin's directive: consistent style, reusable components).
 */

const fjd = (currency?: string) =>
  new Intl.NumberFormat('en-FJ', { style: 'currency', currency: currency ?? 'FJD' });

/**
 * Minutes -> a duration a person reads at a glance. A bare number is unreadable on a board:
 * "79" is not "1h 19m".
 *
 * A NEGATIVE value is meaningful, not an error — slot_remaining_min goes negative once a
 * delivery slot has passed, and the sign is the whole point of the figure.
 */
export const formatMinutes = (raw: number): string => {
  const negative = raw < 0;
  const total = Math.abs(Math.round(raw));
  const days = Math.floor(total / 1440);
  const hours = Math.floor((total % 1440) / 60);
  const minutes = total % 60;

  let text: string;
  if (days > 0) text = `${days}d ${hours}h`;
  else if (hours > 0) text = `${hours}h ${minutes}m`;
  else text = `${minutes}m`;

  return negative ? `-${text}` : text;
};

export const formatCellValue = (
  value: string | number | boolean | null | undefined,
  meta: IAnalyticsColumnMeta,
): string => {
  if (value === null || value === undefined || value === '') return '—';
  switch (meta.type) {
    case 'money':
      return fjd(meta.currency).format(Number(value));
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'number':
      return Number(value).toLocaleString('en-FJ');
    case 'duration':
      return formatMinutes(Number(value));
    case 'date':
      return dayjs(String(value)).format('DD MMM YYYY');
    case 'datetime':
      return dayjs(String(value)).format('DD MMM YYYY, HH:mm');
    case 'boolean':
      return value === true || value === 1 || value === '1' ? 'Yes' : 'No';
    default:
      return String(value);
  }
};

export const toGridColumn = (meta: IAnalyticsColumnMeta): GridColDef => ({
  field: meta.key,
  headerName: meta.label,
  flex: 1,
  minWidth: 130,
  align: ['number', 'money', 'percent', 'duration'].includes(meta.type) ? 'right' : 'left',
  headerAlign: ['number', 'money', 'percent', 'duration'].includes(meta.type) ? 'right' : 'left',
  renderCell: (params) =>
    meta.type === 'enum' ? (
      <Chip size="small" label={formatCellValue(params.value, meta)} />
    ) : (
      formatCellValue(params.value, meta)
    ),
});
